<template>
  <div class="article-admin">
    <div class="action-bar">
      <el-button type="primary" @click="showAddDialog">
        <i class="el-icon-plus"></i> 新建文章
      </el-button>
    </div>

    <!-- 文章列表 -->
    <el-table
      :data="articleList"
      style="width: 100%"
      v-loading="loading"
    >
      <el-table-column
        label="序号"
        width="80"
        type="index"
      ></el-table-column>
      <el-table-column
        prop="title"
        label="标题"
        min-width="200"
      ></el-table-column>
      <el-table-column
        prop="author"
        label="作者"
        width="120"
      >
        <template slot-scope="scope">
          {{ scope.row.author || '-' }}
        </template>
      </el-table-column>
      <el-table-column
        label="封面"
        width="120"
      >
        <template slot-scope="scope">
          <img 
            v-if="scope.row.cover_image" 
            :src="scope.row.cover_image" 
            class="cover-thumbnail"
            @click="previewImage(scope.row.cover_image)"
          />
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="description"
        label="简介"
        min-width="200"
        show-overflow-tooltip
      >
        <template slot-scope="scope">
          {{ scope.row.description || '-' }}
        </template>
      </el-table-column>
      <el-table-column
        prop="status"
        label="状态"
        width="100"
      >
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 'published' ? 'success' : 'info'">
            {{ scope.row.status === 'published' ? '已发布' : '草稿' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column
        prop="publish_time"
        label="发布时间"
        width="120"
      >
        <template slot-scope="scope">
          {{ scope.row.publish_time ? formatDateOnly(scope.row.publish_time) : '-' }}
        </template>
      </el-table-column>
      <el-table-column
        prop="created_at"
        label="创建时间"
        width="180"
      >
        <template slot-scope="scope">
          {{ formatDate(scope.row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column
        label="操作"
        width="250"
        fixed="right"
      >
        <template slot-scope="scope">
          <el-button
            size="mini"
            @click="handleEdit(scope.row)"
          >编辑</el-button>
          <el-button
            size="mini"
            type="success"
            v-if="scope.row.status === 'draft'"
            @click="handlePublish(scope.row)"
          >发布</el-button>
          <el-button
            size="mini"
            type="danger"
            @click="handleDelete(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :current-page="currentPage"
        @current-change="handleCurrentChange"
        @size-change="handleSizeChange"
      ></el-pagination>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog 
      :title="dialogTitle" 
      :visible.sync="dialogVisible" 
      width="90%"
      :close-on-click-modal="false"
      class="article-dialog"
    >
      <el-form :model="articleForm" ref="articleForm" label-width="100px" :rules="rules">
        <el-row :gutter="20">
          <el-col :span="16">
            <el-form-item label="文章标题" prop="title">
              <el-input 
                v-model="articleForm.title" 
                placeholder="请输入文章标题（最多64字）"
                :maxlength="64"
                show-word-limit
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="作者">
              <el-input 
                v-model="articleForm.author" 
                placeholder="请输入作者"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="文章简介">
          <el-input 
            v-model="articleForm.description" 
            type="textarea"
            :rows="3"
            placeholder="请输入文章简介（最多500字）"
            :maxlength="500"
            show-word-limit
          ></el-input>
        </el-form-item>

        <el-form-item label="封面图片">
          <div class="cover-upload-container">
            <el-upload
              class="cover-uploader"
              name="image"
              :action="uploadImageUrl"
              :headers="uploadHeaders"
              :show-file-list="false"
              :on-success="handleCoverUploadSuccess"
              :on-error="handleUploadError"
              :before-upload="beforeImageUpload"
            >
              <img v-if="articleForm.cover_image" :src="articleForm.cover_image" class="cover-image" />
              <i v-else class="el-icon-plus cover-uploader-icon"></i>
            </el-upload>
            <div class="cover-tip">建议尺寸：900x500px，支持JPG、PNG格式</div>
          </div>
        </el-form-item>

        <el-form-item label="文章正文" prop="content" class="editor-form-item">
          <div class="editor-wrapper">
            <div class="editor-container">
              <Toolbar
                class="editor-toolbar"
                :editor="editor"
                :defaultConfig="toolbarConfig"
                :mode="mode"
              />
              <Editor
                class="editor-content"
                v-model="articleForm.content"
                :defaultConfig="editorConfig"
                :mode="mode"
                @onCreated="handleEditorCreated"
              />
            </div>
          </div>
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="articleForm.status">
            <el-radio label="draft">草稿</el-radio>
            <el-radio label="published">发布</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="success" @click="saveDraft">保存草稿</el-button>
        <el-button type="primary" @click="submitForm">保存并{{ articleForm.status === 'published' ? '发布' : '保存' }}</el-button>
      </span>
    </el-dialog>

    <!-- 图片预览对话框 -->
    <el-dialog
      title="图片预览"
      :visible.sync="previewVisible"
      width="50%"
    >
      <img :src="previewImageUrl" style="width: 100%;" />
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request';
import '@wangeditor/editor/dist/css/style.css';
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';

export default {
  name: 'ArticleAdmin',
  components: {
    Editor,
    Toolbar
  },
  data() {
    return {
      articleList: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      loading: false,
      dialogVisible: false,
      dialogTitle: '新建文章',
      isEdit: false,
      previewVisible: false,
      previewImageUrl: '',
      editor: null,
      mode: 'default', // 或 'simple'
      articleForm: {
        id: null,
        title: '',
        author: '',
        description: '',
        cover_image: '',
        content: '',
        status: 'draft'
      },
      rules: {
        title: [
          { required: true, message: '请输入文章标题', trigger: 'blur' },
          { max: 64, message: '标题不能超过64个字符', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入文章正文', trigger: 'blur' }
        ]
      },
      toolbarConfig: {
        // 工具栏配置
        excludeKeys: []
      },
      editorConfig: {
        placeholder: '从这里开始写正文...',
        MENU_CONF: {}
      }
    };
  },
  computed: {
    uploadImageUrl() {
      return '/api/admin/upload-image';
    },
    uploadHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem('adminToken')}`
      };
    }
  },
  created() {
    this.fetchArticleList();
    // 配置编辑器图片上传
    const vm = this;
    this.editorConfig.MENU_CONF.uploadImage = {
      server: '/api/admin/upload-image',
      fieldName: 'image',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('adminToken')}`
      },
      allowedFileTypes: ['image/*'],
      // 自定义上传
      customUpload: async (file, insertFn) => {
        const formData = new FormData();
        formData.append('image', file);
        
        try {
          const response = await request.post('/api/admin/upload-image', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          const url = response.data.url;
          // 插入图片，wangEditor会自动应用样式
          insertFn(url, file.name, url);
        } catch (error) {
          console.error('图片上传失败:', error);
          vm.$message.error('图片上传失败');
        }
      }
    };
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
  methods: {
    // 格式化日期（带时间）
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    },
    
    // 格式化日期（仅年月日）
    formatDateOnly(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },
    
    // 编辑器创建完成
    handleEditorCreated(editor) {
      this.editor = editor;
    },
    
    // 预览图片
    previewImage(url) {
      this.previewImageUrl = url;
      this.previewVisible = true;
    },
    
    // 获取文章列表
    async fetchArticleList() {
      this.loading = true;
      try {
        const response = await request.get('/api/admin/articles', {
          params: {
            page: this.currentPage,
            limit: this.pageSize
          }
        });
        this.articleList = response.data.data;
        this.total = response.data.total;
      } catch (error) {
        console.error('获取文章列表失败:', error);
        this.$message.error('获取文章列表失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 分页处理
    handleCurrentChange(page) {
      this.currentPage = page;
      this.fetchArticleList();
    },
    
    // 每页显示条数改变
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.fetchArticleList();
    },
    
    // 显示添加对话框
    showAddDialog() {
      this.isEdit = false;
      this.dialogTitle = '新建文章';
      this.articleForm = {
        id: null,
        title: '',
        author: '',
        description: '',
        cover_image: '',
        content: '',
        status: 'draft'
      };
      this.dialogVisible = true;
      
      // 等待DOM更新后重置编辑器
      this.$nextTick(() => {
        if (this.editor) {
          this.editor.setHtml('');
        }
      });
    },
    
    // 编辑文章
    async handleEdit(row) {
      this.isEdit = true;
      this.dialogTitle = '编辑文章';
      
      try {
        const response = await request.get(`/api/admin/articles/${row.id}`);
        this.articleForm = { ...response.data };
        this.dialogVisible = true;
        
        // 等待DOM更新后设置编辑器内容
        this.$nextTick(() => {
          if (this.editor) {
            this.editor.setHtml(this.articleForm.content || '');
          }
        });
      } catch (error) {
        console.error('获取文章详情失败:', error);
        this.$message.error('获取文章详情失败');
      }
    },
    
    // 发布文章
    async handlePublish(row) {
      this.$confirm('确定要发布这篇文章吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          // 先获取完整的文章数据（包含content）
          const response = await request.get(`/api/admin/articles/${row.id}`);
          const fullArticle = response.data;
          
          // 更新状态为发布，保持所有原有数据
          const article = { 
            ...fullArticle, 
            status: 'published' 
          };
          
          await request.put(`/api/admin/articles/${row.id}`, article);
          this.$message.success('发布成功');
          this.fetchArticleList();
        } catch (error) {
          console.error('发布文章失败:', error);
          this.$message.error('发布文章失败');
        }
      }).catch(() => {});
    },
    
    // 删除文章
    handleDelete(row) {
      this.$confirm('确定要删除这篇文章吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await request.delete(`/api/admin/articles/${row.id}`);
          this.$message.success('删除成功');
          this.fetchArticleList();
        } catch (error) {
          console.error('删除文章失败:', error);
          this.$message.error('删除文章失败');
        }
      }).catch(() => {});
    },
    
    // 上传前验证
    beforeImageUpload(file) {
      const isImage = file.type.startsWith('image/');
      const isLt5M = file.size / 1024 / 1024 < 5;
      
      if (!isImage) {
        this.$message.error('只能上传图片文件!');
        return false;
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过5MB!');
        return false;
      }
      return true;
    },
    
    // 封面上传成功
    handleCoverUploadSuccess(response) {
      this.articleForm.cover_image = response.url;
      this.$message.success('封面上传成功');
    },
    
    // 上传失败
    handleUploadError(err) {
      console.error('上传失败:', err);
      this.$message.error('上传失败');
    },
    
    // 保存草稿
    saveDraft() {
      this.articleForm.status = 'draft';
      this.submitForm();
    },
    
    // 提交表单
    submitForm() {
      this.$refs.articleForm.validate(async (valid) => {
        if (valid) {
          // 获取编辑器内容
          if (this.editor) {
            this.articleForm.content = this.editor.getHtml();
          }
          
          try {
            if (this.isEdit) {
              // 更新文章
              await request.put(`/api/admin/articles/${this.articleForm.id}`, this.articleForm);
              this.$message.success('文章更新成功');
            } else {
              // 创建文章
              await request.post('/api/admin/articles', this.articleForm);
              this.$message.success('文章创建成功');
            }
            
            this.dialogVisible = false;
            this.fetchArticleList();
          } catch (error) {
            console.error('保存文章失败:', error);
            this.$message.error('保存文章失败');
          }
        } else {
          return false;
        }
      });
    }
  }
};
</script>

<style scoped>
.article-admin {
  padding: 20px;
}

.action-bar {
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-end;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.cover-thumbnail {
  width: 80px;
  height: 50px;
  object-fit: cover;
  cursor: pointer;
  border-radius: 4px;
}

.cover-upload-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.cover-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 300px;
  height: 168px;
}

.cover-uploader:hover {
  border-color: #409EFF;
}

.cover-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 300px;
  height: 168px;
  line-height: 168px;
  text-align: center;
}

.cover-image {
  width: 300px;
  height: 168px;
  display: block;
  object-fit: cover;
}

.cover-tip {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}

/* 富文本编辑器样式调整 - 微信公众号风格 */
.editor-wrapper {
  display: flex;
  justify-content: center;
  padding: 20px 0;
  background: #f5f5f5;
}

.editor-container {
  width: 1020px;
  max-width: 100%;
  background: #fff;
  border: 1px solid #e7e7e7;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: visible;
  display: flex;
  flex-direction: column;
}

.editor-toolbar {
  border-bottom: 1px solid #e7e7e7;
  background: #fafafa;
  position: sticky;
  top: -20px;
  margin-top: -20px;
  padding-top: 20px;
  z-index: 10;
  flex-shrink: 0;
  /* 确保工具栏在滚动时完全固定在顶部，不露出 */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.editor-content {
  flex: 1;
  overflow: visible;
  min-height: 500px;
}

/* 编辑器内容区域样式 */
::v-deep .w-e-text-container {
  min-height: 600px;
  padding: 20px;
  background: #fff;
  overflow: visible;
}

/* 编辑器内容样式 - 限制内容宽度，居中显示 */
::v-deep .w-e-text-container .w-e-text {
  max-width: 100%;
  margin: 0 auto;
}

/* 图片自适应宽度 - 微信公众号风格 */
::v-deep .w-e-text-container img {
  max-width: 100% !important;
  width: auto !important;
  height: auto !important;
  display: block;
  margin: 15px auto;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 确保图片在编辑时和预览时大小一致 */
::v-deep .w-e-text-container .w-e-image-container img {
  max-width: 100% !important;
  width: auto !important;
  height: auto !important;
}

/* 段落样式 */
::v-deep .w-e-text-container p {
  margin: 10px 0;
  line-height: 1.8;
  font-size: 16px;
  color: #333;
}

/* 标题样式 */
::v-deep .w-e-text-container h1,
::v-deep .w-e-text-container h2,
::v-deep .w-e-text-container h3,
::v-deep .w-e-text-container h4,
::v-deep .w-e-text-container h5,
::v-deep .w-e-text-container h6 {
  margin: 20px 0 10px;
  font-weight: bold;
  line-height: 1.5;
}

::v-deep .w-e-text-container h1 {
  font-size: 24px;
}

::v-deep .w-e-text-container h2 {
  font-size: 22px;
}

::v-deep .w-e-text-container h3 {
  font-size: 20px;
}

/* 列表样式 */
::v-deep .w-e-text-container ul,
::v-deep .w-e-text-container ol {
  margin: 10px 0;
  padding-left: 30px;
}

::v-deep .w-e-text-container li {
  margin: 5px 0;
  line-height: 1.8;
}

/* 引用样式 */
::v-deep .w-e-text-container blockquote {
  margin: 10px 0;
  padding: 10px 15px;
  border-left: 4px solid #ddd;
  background: #f5f5f5;
  color: #666;
}

/* 代码块样式 */
::v-deep .w-e-text-container pre {
  margin: 10px 0;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 4px;
  overflow-x: auto;
}

/* 表格样式 */
::v-deep .w-e-text-container table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
}

::v-deep .w-e-text-container table td,
::v-deep .w-e-text-container table th {
  border: 1px solid #ddd;
  padding: 8px;
}

/* 工具栏样式 */
::v-deep .w-e-toolbar {
  flex-wrap: wrap;
  padding: 8px;
}

/* 确保居中对齐按钮显示 */
::v-deep .w-e-toolbar .w-e-bar-item {
  display: inline-flex;
  align-items: center;
}

/* 自定义居中按钮样式（如果需要） */
::v-deep .w-e-toolbar .w-e-bar-item[data-menu-key="justifyCenter"] {
  display: inline-flex;
}

/* 对话框样式 */
.article-dialog ::v-deep .el-dialog__body {
  max-height: 80vh;
  overflow-y: auto;
  padding: 20px;
  /* 确保只有一个滚动条 */
  position: relative;
}

/* 编辑器表单项样式 */
.editor-form-item {
  margin-bottom: 0;
}

.editor-form-item ::v-deep .el-form-item__content {
  margin-left: 0 !important;
}

/* 移除编辑器内部的滚动条 */
.article-dialog ::v-deep .w-e-text-container {
  overflow: visible !important;
}

.article-dialog ::v-deep .w-e-text-container .w-e-text {
  overflow: visible !important;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .editor-container {
    width: 100%;
    margin: 0 10px;
  }
  
  ::v-deep .w-e-text-container {
    padding: 15px;
  }
}
</style>

